import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Trash2, ArrowDownCircle, ArrowUpCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const CATS = ["refunds", "stripe_fees", "hosting", "ai_api", "email", "software", "advertising",
  "referral_rewards", "affiliate_commissions", "contractors_payroll", "professional_services", "other"];
const PRESETS = [
  { label: "Hosting", category: "hosting" },
  { label: "AI / API", category: "ai_api" },
  { label: "Email", category: "email" },
  { label: "Software", category: "software" },
];
const money = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Cell({ label, value, tone }) {
  const color = tone === "in" ? "text-[#10B981]" : tone === "out" ? "text-[#EF4444]" : (value < 0 ? "text-[#EF4444]" : "text-white");
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4" data-testid={`cf-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`text-2xl font-heading ${color}`}>{money(value)}</div>
      <div className="text-xs text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

export default function AdminCashflow() {
  const [cf, setCf] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("hosting");
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([api.get("/admin/cashflow"), api.get("/admin/expenses")]);
      setCf(c.data); setExpenses(e.data.expenses || []);
    } catch (e) { toast.error("Failed to load cash flow"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addExpense = async () => {
    const a = parseFloat(amt);
    if (!a || a <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post("/admin/expenses", { category: cat, amount: a, note, recurring });
      setAmt(""); setNote(""); setRecurring(false); toast.success("Expense added"); load();
    } catch (e) { toast.error("Failed to add expense"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/admin/expenses/${id}`); load(); } catch (e) { toast.error("Delete failed"); } };

  const applyPreset = (p) => { setCat(p.category); setRecurring(true); };

  if (loading || !cf) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  const { inflow, outflow, net } = cf;
  const recurringExpenses = expenses.filter((e) => e.recurring);
  const recurringMonthly = recurringExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-cashflow">
      <div>
        <h2 className="font-heading text-2xl text-white">Cash Flow</h2>
        <p className="text-sm text-[#71717A]">Cash collected (REAL live-Stripe payments only) minus TRACKED expenses. Separate from MRR (recurring run-rate). Seed/demo/test records are excluded.</p>
      </div>
      {cf.unverified?.ytd > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-[#0A0A0B] p-3" data-testid="cf-unverified">
          <Info className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#71717A]"><span className="text-[#A1A1AA]">{money(cf.unverified.ytd)}</span> in seed/demo/test payment records is <b>excluded</b> from the figures below (not verified against live Stripe).</p>
        </div>
      )}

      {/* Morning view */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Cell label="Cash Collected Today" value={inflow.today} tone="in" />
        <Cell label="Cash Out Today" value={outflow.today} tone="out" />
        <Cell label="Net Cash Flow Today" value={net.today} />
        <Cell label="Cash Collected Month" value={inflow.month} tone="in" />
        <Cell label="Cash Out Month" value={outflow.month} tone="out" />
        <Cell label="Net Cash Flow Month" value={net.month} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Cell label="Collected This Week" value={inflow.week} tone="in" />
        <Cell label="Monthly-plan Cash (mo)" value={inflow.monthly_plan} tone="in" />
        <Cell label="Annual-plan Cash (mo)" value={inflow.annual_plan} tone="in" />
        <Cell label="Net This Week" value={net.week} />
        <Cell label="YTD Collected" value={inflow.ytd} tone="in" />
        <Cell label="YTD Out (tracked)" value={outflow.ytd} tone="out" />
        <Cell label="YTD Net" value={net.ytd} />
        <Cell label="All-time Collected" value={inflow.all_time} tone="in" />
      </div>

      {/* Expense entry */}
      <div className="rounded-xl border border-white/10 bg-[#121214] p-5" data-testid="cf-expense-form">
        <div className="flex items-center gap-2 mb-3"><ArrowUpCircle className="w-4 h-4 text-[#EF4444]" /><h3 className="font-heading text-lg text-white">Log an operating expense</h3></div>
        <p className="text-xs text-[#71717A] mb-4">Outflows are based on TRACKED expenses you enter here. Nothing is auto-purchased or connected.</p>
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="cf-presets">
          <span className="text-xs text-[#71717A]">Quick add recurring:</span>
          {PRESETS.map((p) => (
            <button key={p.category} data-testid={`cf-preset-${p.category}`} onClick={() => applyPreset(p)}
              className="text-xs px-3 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-4 gap-2">
          <select data-testid="cf-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-md bg-[#0A0A0B] border border-white/10 text-white h-10 px-3 text-sm">
            {CATS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <Input data-testid="cf-amt" type="number" placeholder="Amount ($)" value={amt} onChange={(e) => setAmt(e.target.value)} className="bg-[#0A0A0B] border-white/10 text-white" />
          <Input data-testid="cf-note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#0A0A0B] border-white/10 text-white" />
          <Button onClick={addExpense} disabled={saving} data-testid="cf-add" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
        <label className="flex items-center gap-2 mt-3 text-xs text-[#A1A1AA] cursor-pointer" data-testid="cf-recurring-label">
          <input type="checkbox" data-testid="cf-recurring" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="accent-[#D4AF37]" />
          Recurring monthly cost (auto-counted in every month going forward)
        </label>
        {expenses.length > 0 && (
          <div className="mt-4 space-y-2">
            {expenses.map((e) => (
              <div key={e.expense_id} className="flex items-center justify-between rounded-lg bg-[#0A0A0B] border border-white/5 p-3 text-sm" data-testid={`cf-exp-${e.expense_id}`}>
                <span className="text-[#D4D4D8]">{e.category.replace(/_/g, " ")} · <span className="text-[#EF4444]">{money(e.amount)}</span>{e.recurring ? <span className="ml-1 text-[10px] uppercase tracking-wide text-[#D4AF37] border border-[#D4AF37]/30 rounded px-1 py-0.5">recurring</span> : null}{e.note ? ` · ${e.note}` : ""}</span>
                <button onClick={() => del(e.expense_id)} className="text-[#71717A] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recurring costs manager */}
      <div className="rounded-xl border border-white/10 bg-[#121214] p-5" data-testid="cf-recurring-manager">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-lg text-white">Recurring costs</h3>
          <span className="text-sm text-[#D4AF37]" data-testid="cf-recurring-total">{money(recurringMonthly)}/mo</span>
        </div>
        <p className="text-xs text-[#71717A] mb-3">Saved once, automatically counted in every month's cash flow and estimated profit going forward.</p>
        {recurringExpenses.length === 0
          ? <p className="text-sm text-[#52525B]">No recurring costs yet — use the quick-add buttons above.</p>
          : (
            <div className="space-y-2">
              {recurringExpenses.map((e) => (
                <div key={e.expense_id} className="flex items-center justify-between rounded-lg bg-[#0A0A0B] border border-white/5 p-3 text-sm" data-testid={`cf-recurring-${e.expense_id}`}>
                  <span className="text-[#D4D4D8] capitalize">{e.category.replace(/_/g, " ")} · <span className="text-[#EF4444]">{money(e.amount)}/mo</span>{e.note ? ` · ${e.note}` : ""}</span>
                  <button onClick={() => del(e.expense_id)} className="text-[#71717A] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
